// components/ActivityTracker/ActivityForm.jsx — Create / Edit activity dialog
import React, { useState, useEffect } from "react";
import {
  Avatar, Button, Dialog, DialogActions, DialogBody, DialogContent,
  DialogSurface, DialogTitle, DialogTrigger, Field, Input,
  Select, Spinner, Textarea, Text,
} from "@fluentui/react-components";
import { Warning24Regular, LockClosed16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useDeptOwnerMap } from "../../hooks/useDeptOwnerMap";
import PeoplePicker from "../shared/PeoplePicker";

export const CATEGORIES = ["Procurement", "Logistics", "Contracts", "HSE", "Projects", "General"];
export const PRIORITIES = ["High", "Medium", "Low"];
export const STATUSES   = ["Not Started", "In Progress", "On Hold", "Completed"];
export const ENTITIES   = ["276", "Otakikpo", "CLNG", "NIG.", "N/A"];
// USER_DEPTS kept for backward compat (filter dropdown falls back to this)
export const USER_DEPTS = [];

const EMPTY = {
  title: "", description: "", category: "Contracts", priority: "Medium",
  status: "Not Started", dueDate: "",
  userDept: "", entity: "", contractor: "", lookAhead: "",
  owners: [],
  ownerEmail: "", ownerName: "", ownerJobTitle: "", ownerDepartment: "",
  assignedTo: "", assignedToName: "",
};

function toPickerUser(email, name, jobTitle = "", department = "") {
  if (!email && !name) return null;
  return { id: "", name: name || email, email: email || "", jobTitle: jobTitle || "", department: department || "" };
}

export default function ActivityForm({ open, onClose, onSave, initial, currentUserEmail, currentUserName }) {
  const { isAdmin, isManager, isEditor } = useUserAccess();
  const canChangeAssignee = isAdmin || isManager || isEditor;
  const { map, departments, loading: deptsLoading } = useDeptOwnerMap();

  const [form, setForm]           = useState(EMPTY);
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [assignedUser, setAssignedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (initial) {
      setForm({ ...EMPTY, ...initial });
      // Prefer the owners array; fall back to single legacy fields
      const initOwners = initial.owners?.length > 0
        ? initial.owners
        : (initial.ownerEmail ? [toPickerUser(initial.ownerEmail, initial.ownerName, initial.ownerJobTitle, initial.ownerDepartment)].filter(Boolean) : []);
      setOwnerUsers(initOwners);
      setAssignedUser(toPickerUser(initial.assignedTo, initial.assignedToName));
    } else {
      setForm({ ...EMPTY });
      setOwnerUsers([]);
      setAssignedUser({ id: "", name: currentUserName, email: currentUserEmail, jobTitle: "", department: "" });
    }
  }, [open, initial, currentUserEmail, currentUserName]);

  function set(key, value) { setForm((p) => ({ ...p, [key]: value })); }

  function handleAssigneeChange(user) {
    setAssignedUser(user);
    set("assignedToName", user?.name  || "");
    set("assignedTo",     user?.email || "");
  }

  function handleDeptChange(dept) {
    set("userDept", dept);
  }

  async function handleSave() {
    if (!form.title.trim())   { setError("Task title is required."); return; }
    if (!form.userDept)       { setError("User Department is required."); return; }
    if (!assignedUser?.email) { setError("You must assign this task to an SCM staff member."); return; }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        title:           form.title.trim(),
        owners:          ownerUsers,
        ownerEmail:      ownerUsers[0]?.email      || "",
        ownerName:       ownerUsers[0]?.name       || "",
        ownerJobTitle:   ownerUsers[0]?.jobTitle   || "",
        ownerDepartment: ownerUsers[0]?.department || "",
        assignedTo:      assignedUser?.email   || "",
        assignedToName:  assignedUser?.name    || "",
        createdBy:       initial?.createdBy     || currentUserEmail,
        createdByName:   initial?.createdByName || currentUserName,
        updates: initial?.updates || [],
      };
      if (!initial) {
        payload.updates = [{
          id: `u-${Date.now()}`,
          date: new Date().toISOString(),
          by: currentUserEmail,
          byName: currentUserName,
          status: payload.status,
          note: `Task created and assigned to ${assignedUser.name || assignedUser.email}.`,
          lookAhead: payload.lookAhead || "",
        }];
      }
      await onSave(payload);
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save task.");
    } finally {
      setSaving(false);
    }
  }

  const isEdit = !!initial;

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 640, width: "95vw" }}>
        <DialogBody>
          <DialogTitle>{isEdit ? "Edit Activity" : "New Activity / Task"}</DialogTitle>
          <DialogContent>
            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>

              {/* TASK DETAILS */}
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#0066FF", letterSpacing: "0.6px", borderBottom: "1px solid #EDEBE9", paddingBottom: 6 }}>TASK DETAILS</Text>

              <Field label="Description (Task / Contract Title)" required>
                <Input
                  value={form.title}
                  onChange={(_, d) => set("title", d.value)}
                  placeholder="e.g. Contract for Provision of Turnkey Drilling Services"
                />
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="User Department" required hint={deptsLoading ? "Loading departments from O365…" : undefined}>
                  <Select
                    value={form.userDept}
                    onChange={(_, d) => handleDeptChange(d.value)}
                    disabled={deptsLoading}
                  >
                    <option value="">{deptsLoading ? "Loading…" : "Select department…"}</option>
                    {departments.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Entity / Asset">
                  <Select value={form.entity} onChange={(_, d) => set("entity", d.value)}>
                    <option value="">Select entity…</option>
                    {ENTITIES.map((e) => <option key={e} value={e}>{e}</option>)}
                  </Select>
                </Field>
              </div>

              <Field label="Contractor / Vendor">
                <Input
                  value={form.contractor}
                  onChange={(_, d) => set("contractor", d.value)}
                  placeholder="e.g. Bill Energy Services Limited"
                />
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="Category">
                  <Select value={form.category} onChange={(_, d) => set("category", d.value)}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={form.priority} onChange={(_, d) => set("priority", d.value)}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </Select>
                </Field>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="Status">
                  <Select value={form.status} onChange={(_, d) => set("status", d.value)}>
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </Select>
                </Field>
                <Field label="Due Date">
                  <Input type="date" value={form.dueDate || ""} onChange={(_, d) => set("dueDate", d.value)} />
                </Field>
              </div>

              <Field label="Additional Notes">
                <Textarea
                  value={form.description}
                  onChange={(_, d) => set("description", d.value)}
                  placeholder="Background context, scope, or any additional details…"
                  rows={2}
                />
              </Field>

              {/* LOOK AHEAD */}
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#006E6E", letterSpacing: "0.6px", borderBottom: "1px solid #EDEBE9", paddingBottom: 6, marginTop: 4 }}>LOOK AHEAD</Text>

              <Field label="Look Ahead / Next Steps">
                <Textarea
                  value={form.lookAhead}
                  onChange={(_, d) => set("lookAhead", d.value)}
                  placeholder="What is expected to happen next, upcoming milestones…"
                  rows={2}
                />
              </Field>

              {/* OWNER — multi-select */}
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#B8430A", letterSpacing: "0.6px", borderBottom: "1px solid #EDEBE9", paddingBottom: 6, marginTop: 4 }}>CONTRACT / TASK OWNER</Text>
              <Text style={{ fontSize: 12, color: "#888", marginTop: -8 }}>
                Select one or more owners for this contract. Owners receive activity update email notifications.
              </Text>

              {/* Selected owner chips */}
              {ownerUsers.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "10px 12px", background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10 }}>
                  {ownerUsers.map((u, i) => (
                    <div
                      key={u.email || i}
                      style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #C0D4FF", borderRadius: 20, padding: "4px 10px 4px 6px" }}
                    >
                      <Avatar name={u.name} size={20} color="brand" />
                      <div style={{ lineHeight: 1.2 }}>
                        <Text style={{ fontSize: 12, fontWeight: 700, color: "#001A3D", display: "block" }}>{u.name}</Text>
                        <Text style={{ fontSize: 11, color: "#0066FF", display: "block" }}>{u.email}</Text>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOwnerUsers((prev) => prev.filter((_, j) => j !== i))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#AAA", padding: "2px 0 2px 4px", display: "flex", alignItems: "center", borderRadius: 4 }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#D13438"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "#AAA"; }}
                      >
                        <Dismiss16Regular style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search to add owners */}
              <PeoplePicker
                label={ownerUsers.length === 0 ? "Owner" : "Add another owner"}
                value={null}
                onChange={(user) => {
                  if (!user || ownerUsers.some((u) => u.email === user.email)) return;
                  setOwnerUsers((prev) => [...prev, user]);
                }}
                placeholder="Search O365 for contract/task owner…"
              />

              {/* ASSIGNED TO */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #EDEBE9", paddingBottom: 6, marginTop: 4 }}>
                <Text style={{ fontSize: 11, fontWeight: 700, color: "#007C3E", letterSpacing: "0.6px" }}>ASSIGNED TO (SCM STAFF)</Text>
                {!canChangeAssignee && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#888", fontSize: 11 }}>
                    <LockClosed16Regular /><span>Auto-assigned</span>
                  </div>
                )}
              </div>
              <Text style={{ fontSize: 12, color: "#888", marginTop: -8 }}>
                {canChangeAssignee ? "The SCM staff member responsible for this task." : "Auto-set to you as the task creator."}
              </Text>
              <PeoplePicker
                label="Assigned To"
                required
                value={assignedUser}
                onChange={canChangeAssignee ? handleAssigneeChange : undefined}
                disabled={!canChangeAssignee}
                placeholder="Search for SCM staff member…"
              />

              {error && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FDE7E9", borderRadius: 8, color: "#D13438", fontSize: 13 }}>
                  <Warning24Regular style={{ flexShrink: 0 }} />{error}
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" disabled={saving}>Cancel</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={saving || !assignedUser?.email}
              style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}
            >
              {saving ? <Spinner size="tiny" /> : isEdit ? "Save Changes" : "Create Task"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
